<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="xl"
            @cancel="cancel"
            @close="cancel"
            no-close-on-esc
            no-close-on-backdrop
            @shown="onModalShown"
        >

            <template #modal-title>
                Edit Media - ID: {{ selectedMediaModel.id }} Title: {{ selectedMediaModel.title }}
            </template>
            <div v-if="alternate_media_loading" class="d-flex justify-content-center">
                <b-spinner label="Loading..."></b-spinner>
            </div>
            <div v-else>
                <b-container class="bv-example-row">
                    <b-row class="justify-content-md-center">
                        <b-col col lg="2" class="d-flex justify-content-end align-items-center p-0">
                            <div>Languages</div>
                            <b-dropdown v-if="can_update_media"
                                        variant="light" no-caret right toggle-class="p-0"
                                        class="mx-2"
                                        :disabled="has_alt_media"
                                        id="add-alt-media-dropdown"
                            >
                                <template v-slot:button-content>
                                    <font-awesome-icon
                                        :icon="['fad', 'plus-square']" size="lg"
                                        :class="{'add-language text-success': !has_alt_media}"
                                    />
                                </template>
                                <b-dropdown-item
                                    v-if="can_link_media"
                                    @click="addAlternateMedia"
                                >
                                    Link to Existing Media
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="can_link_media && can_create_media"
                                    @click="createAlternateMedia"
                                >
                                    Create New Language Media
                                </b-dropdown-item>
                            </b-dropdown>
                            <b-tooltip v-if="has_alt_media" target="add-alt-media-dropdown">
                                Cannot Add Alternate Language Media When One Already Exists
                            </b-tooltip>
                        </b-col>
                        <b-col cols="auto" class="d-flex justify-content-center align-items-center p-0">
                            <div v-for="(media, index) in static_media_array" :key="`alternate-media-${index}`"
                                 style="font-size:2rem" class="language-flag cursor-pointer mx-2 position-relative"
                                 @click="selectMedia(index)">
                                <flag :iso="getFlagIso(media.language)" />
                                <div
                                    v-if="index === selected_media_index"
                                    :style="{ borderBottom: '3px solid black', position: 'absolute', bottom: 0, left: 0, right: 0 }"
                                ></div>
                            </div>
                        </b-col>
                        <b-col col lg="2" class="d-flex justify-content-start align-items-center p-0">
                            <b-btn v-if="can_unlink_media && static_media_array.length > 1" variant="primary" size="sm"
                                   @click="unlinkFromAlternateMedia">
                                Unlink Language
                            </b-btn>
                        </b-col>
                        <b-btn
                            :variant="copy_to_clipboard_btn_variant"
                            :class="{'active': copying_to_clipboard}"
                            content="Copy URL to Clipboard"
                            v-tippy="{placement: 'top'}"
                            @click="copyToClipboard(selectedMediaModel)"
                            :disabled="copying_to_clipboard"
                            class="my-2 position-absolute copy-btn"
                            size="sm"
                        >
                            <span v-if="copying_to_clipboard">
                                <font-awesome-icon :icon="['fas', 'check']" size="lg" fixed-width />
                            </span>
                            <span v-else>
                                <font-awesome-icon :icon="['fad', 'clipboard']" size="lg" fixed-width />
                            </span>
                        </b-btn>
                    </b-row>
                </b-container>

                <div v-if="!has_english_alt && selectedMediaModel.type == 'video'" class="alert alert-danger my-2"
                     role="alert">
                    All {{ languageLabel }} language videos should have an alternate English version. This media is
                    {{ languageLabel }} and lacks an English version.
                </div>
            </div>

            <b-container class="bv-example-row mt-4 mb-2">
                <b-row v-if="formattedCreatedDate || formattedUpdatedDate" class="mb-2">
                    <b-col>
                        <div class="d-flex flex-wrap small text-muted">
                            <span v-if="formattedCreatedDate" class="mr-4">
                                <strong>Created:</strong> {{ formattedCreatedDate }}<span v-if="createdByLabel"> by {{ createdByLabel }}</span>
                            </span>
                            <span v-if="formattedUpdatedDate">
                                <strong>Updated:</strong> {{ formattedUpdatedDate }}<span v-if="updatedByLabel"> by {{ updatedByLabel }}</span>
                            </span>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group
                            v-if="can_update_media"
                            label="Language"
                            label-for="language"
                        >
                            <multiselect
                                v-model="selectedMediaModel.language"
                                :options="languageOptions"
                                :searchable="false"
                            >
                                <template #option="{ option }">
                                    <span class="d-flex align-items-center">
                                        <div class="mr-1">
                                            <flag v-if="getFlagIso(option)" :iso="getFlagIso(option)" />
                                            <div v-else-if="!option || option == ''">
                                                N/A
                                            </div>
                                        </div>
                                        <div class="text-uppercase">{{ option }}</div>
                                    </span>
                                </template>
                                <template #singleLabel="{ option }">
                                    <span class="d-flex align-items-center">
                                        <div class="mr-1">
                                            <flag v-if="getFlagIso(option)" :iso="getFlagIso(option)" />
                                            <div v-else-if="!option || option == ''">
                                                N/A
                                            </div>
                                        </div>
                                        <div class="text-uppercase">{{ option }}</div>
                                    </span>
                                </template>
                            </multiselect>
                            <b-form-invalid-feedback :state="!selectedMediaModel.language ? false : null">
                                language is required
                            </b-form-invalid-feedback>
                            <b-form-invalid-feedback :state="languageCheck(selectedMediaModel.language)">
                                language cannot be changed to a language that is already linked to this media
                            </b-form-invalid-feedback>
                        </b-form-group>
                        <div class="mb-3 w-100" v-if="can_update_media">
                            <div class="my-2">New Media</div>
                            <small class="text-muted d-block mb-1">Maximum file size 300 MB.</small>
                            <div class="row">
                                <div :class="selectedMediaModel.url ? 'col-9' : 'col-12'">
                                    <b-form-file
                                        v-model="submit_data.file"
                                        placeholder="Choose a file or drop it here..."
                                        drop-placeholder="Drop file here..."
                                        accept="image/*,audio/*,video/*,application/zip"
                                    />
                                </div>
                                <div v-if="selectedMediaModel.url" class="col-3">
                                    <b-btn
                                        variant="outline-primary"
                                        class="d-block w-100"
                                        :href="`/api/media/download/${selectedMediaModel.id}`"
                                    >
                                        <font-awesome-icon
                                            :icon="['fad', 'download']"
                                            fixed-width
                                        />
                                        Download Media
                                    </b-btn>
                                </div>
                            </div>
                            <b-alert
                                v-if="upload_file_size_alert"
                                variant="warning"
                                class="mt-2 mb-0"
                                show
                            >
                                {{ upload_file_size_alert }}
                            </b-alert>
                        </div>
                        <b-form-group
                            v-if="can_update_media"
                            label="Default Splash Image"
                            label-for="splash_default_key"
                        >
                            <b-form-select
                                v-model="submit_data.splash_default_key"
                                @change="onDefaultSplashChange"
                            >
                                <b-form-select-option :value="null">None / Custom</b-form-select-option>
                                <b-form-select-option
                                    v-for="opt in default_splash_options"
                                    :key="opt.key"
                                    :value="opt.key"
                                >
                                    {{ opt.label }}
                                </b-form-select-option>
                            </b-form-select>
                            <b-alert
                                v-if="default_splash_load_failed"
                                variant="warning"
                                class="mt-2 mb-0"
                                show
                            >
                                Some default splash options could not be loaded. You can still upload a custom splash image.
                            </b-alert>
                        </b-form-group>
                        <div class="mb-3" v-if="can_update_media">
                            <div class="my-2">
                                Splash Image
                                <b-badge
                                    v-if="!selectedMediaModel.splash_url"
                                    pill
                                    variant="info"
                                >
                                    Not set
                                </b-badge>
                            </div>
                            <div class="row">
                                <div :class="selectedMediaModel.splash_url ? 'col-9' : 'col-12'">
                                    <b-form-file
                                        v-model="submit_data.splash_file"
                                        :disabled="!!submit_data.splash_default_key"
                                        @input="onSplashFileChange"
                                        placeholder="Choose a file or drop it here..."
                                        drop-placeholder="Drop file here..."
                                        accept="image/*"
                                    />
                                </div>
                                <div v-if="selectedMediaModel.splash_url" class="col-3">
                                    <b-btn
                                        variant="outline-primary"
                                        class="d-block"
                                        :href="selectedMediaModel.splash_url"
                                        target="_blank"
                                    >
                                        <font-awesome-icon
                                            :icon="['fad', 'external-link']"
                                            fixed-width
                                        />
                                        Open Current Splash
                                    </b-btn>
                                </div>
                            </div>

                        </div>
                        <div class="mb-3" v-if="can_update_media && selectedMediaModel.type === 'video'">
                            <div class="my-2">
                                Closed Captions / Subtitle File
                                <b-badge
                                    v-if="!selectedMediaModel.caption_url"
                                    pill
                                    variant="info"
                                >
                                    Not set
                                </b-badge>
                            </div>
                            <div class="row">
                                <div :class="selectedMediaModel.caption_url ? 'col-9' : 'col-12'">
                                    <b-form-file
                                        v-model="submit_data.caption_file"
                                        placeholder="Choose a file or drop it here..."
                                        drop-placeholder="Drop file here..."
                                        accept=".vtt,.srt"
                                    />
                                </div>
                                <div v-if="selectedMediaModel.caption_url" class="col-3">
                                    <b-btn
                                            variant="outline-primary"
                                            class="d-block"
                                            :href="selectedMediaModel.caption_url"
                                            target="_blank"
                                        >
                                            <font-awesome-icon
                                                :icon="['fad', 'external-link']"
                                                fixed-width
                                            />
                                            Open Current Captions
                                        </b-btn>
                                </div>
                            </div>
                        </div>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col cols="7">
                        <b-form-group
                            label="Title"
                            label-for="title"
                        >
                            <b-form-input
                                type="text"
                                v-model="selectedMediaModel.title"
                                :disabled="!can_update_media"
                            />
                            <b-form-invalid-feedback :state="!selectedMediaModel.title ? false : null">
                                Title is required
                            </b-form-invalid-feedback>
                        </b-form-group>
                        <b-form-group v-if="selectedMediaModel.type === 'image'">
                            <label>
                                Merlin ID(s) <sup>
                                <font-awesome-icon
                                    :icon="['fad', 'info-circle']"
                                    content="For multiple Merlin ids, use comma separated values"
                                    class="text-secondary"
                                    v-tippy="{placement: 'top'}"
                                />
                            </sup>
                            </label>
                            <b-form-input
                                v-model="selectedMediaModel.merlin_ids"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>
                        <b-form-group
                            label="Tags"
                            label-for="tags"
                        >
                            <tag-multiselect
                                v-model="selectedMediaModel.tags"
                                :options="tags"
                                @select="addTag"
                                @remove="removeTag"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col class="d-flex align-items-center justify-content-center position-relative p-0">
                        <div v-if="selectedMediaModel.type === 'image'" class="mb-lg-1">
                            <b-link :href="thumbnail" target="_blank">
                                <b-img :src="thumbnail" thumbnail style="max-height: 250px;" />
                            </b-link>
                        </div>
                        <div v-else-if="selectedMediaModel.type === 'video'" class="mb-3">
                            <video ref="videoAudioElement" width="100%" height="auto" controls crossorigin="anonymous">
                                <source :src="selectedMediaModel.url" type="video/mp4">
                                <track
                                    v-if="selectedMediaModel.caption_url"
                                    default
                                    kind="captions"
                                    :srclang="selectedMediaModel.language"
                                    :src="selectedMediaModel.caption_url"
                                    label="Default"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                        <div v-else-if="selectedMediaModel.type === 'audio'" class="mb-3">
                            <audio ref="videoAudioElement" class="d-flex mb-0" controls>
                                <source :src="selectedMediaModel.url" type="audio/mpeg">
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                        <div v-else-if="selectedMediaModel.type === 'sims'" class="mb-3">
                            <iframe :src="selectedMediaModel.url" />
                            <b-btn
                                variant="outline-primary"
                                class="d-block"
                                :href="selectedMediaModel.url"
                                target="_blank"
                            >
                                <font-awesome-icon
                                    :icon="['fad', 'external-link']"
                                    fixed-width
                                />
                                Open in new tab
                            </b-btn>
                        </div>

                        <div v-if="selectedMediaModel.splash_url" class="position-absolute splash-img-container p-2">
                            <b-link :href="selectedMediaModel.splash_url" target="_blank"
                                    content="Click to view splash image in new tab" v-tippy="{placement: 'top'}">
                                <b-img :src="selectedMediaModel.splash_url" class="splash-img" style="height: 25px;" />
                            </b-link>
                        </div>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-form-group
                            label="Description"
                            label-for="description"
                            v-if="selectedMediaModel.type !== 'sims'"
                        >
                            <b-form-textarea
                                v-model="selectedMediaModel.description"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>

                        <b-form-group
                            v-if="selectedMediaModel.type == 'image'"
                            label="Alt"
                            label-for="alt"
                        >
                            <b-form-textarea
                                v-model="selectedMediaModel.alt"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_media"
                            />
                            <b-form-invalid-feedback :state="!selectedMediaModel.alt ? false : null">
                                Alt tag is required for images
                            </b-form-invalid-feedback>
                        </b-form-group>


                        <b-form-group
                            label="Caption"
                            label-for="caption"
                            v-if="selectedMediaModel.type !== 'sims'"
                        >
                            <b-form-textarea
                                v-model="selectedMediaModel.caption"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>

                        <b-form-group v-if="selectedMediaModel.type !== 'sims'">
                            <div class="d-flex justify-content-between">
                                <span class="mb-2">Credit</span>
                                <b-form-checkbox
                                    @change="handlePublicDomainToggle"
                                    v-model="selectedMediaModel.is_public_domain"
                                    switch
                                    :disabled="!can_update_media"
                                >
                                    Public Domain
                                </b-form-checkbox>
                            </div>
                            <b-form-textarea
                                v-model="selectedMediaModel.credit"
                                rows="2"
                                max-rows="6"
                                :disabled="selectedMediaModel.is_public_domain || !can_update_media"
                            />
                        </b-form-group>

                        <b-form-group>
                            <div class="d-flex justify-content-between">
                                <span class="mb-2">Designer Notes</span>
                            </div>
                            <b-form-textarea
                                v-model="selectedMediaModel.designer_notes"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>

                        <b-form-group>
                            <div class="d-flex justify-content-between">
                                <span class="mb-2">Original File Name</span>
                            </div>
                            <b-form-input
                                type="text"
                                v-model="selectedMediaModel.merlin_title"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>

                        <b-form-group>
                            <div class="d-flex justify-content-between">
                                <span class="mb-2">Expiration Date</span>
                            </div>
                            <div id="exp-date-input">
                                <input
                                    type="date"
                                    class="form-control"
                                    :class="{ 'bg-danger text-white': isExpired }"
                                    :value="(selectedMediaModel.expiration_date || '').slice(0,10)"
                                    @input="onMediaExpirationDateInput"
                                />
                            </div>
                        </b-form-group>

                        <b-form-group
                            label="Expiration Date Info"
                            label-for="expiration_date_info"
                        >
                            <b-form-textarea
                                v-model="selectedMediaModel.expiration_date_info"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>

                        <b-form-group>
                            <div class="d-flex justify-content-between">
                                <span class="mb-2">Media Restriction</span>
                            </div>
                            <span>
                                <b-form-checkbox
                                    class="mb-2"
                                    v-model="selectedMediaModel.is_restricted"
                                    :disabled="false"
                                    switch
                                >
                                    Restricted
                                </b-form-checkbox>
                            </span>
                            <b-form-input
                                type="text"
                                v-model="selectedMediaModel.restricted_description"
                                :disabled="!can_update_media"
                            />
                        </b-form-group>

                        <b-form-group>
                            <span class="mb-2">QR Code</span>
                            <div>
                                <b-btn
                                    v-if="selectedMediaModel.qr_code_url"
                                    variant="outline-primary"
                                    class="my-2 mx-2"
                                    size="sm"
                                    :href="selectedMediaModel.qr_code_url"
                                    target="_blank"
                                >
                                    PNG
                                    <font-awesome-icon :icon="['fas', 'external-link']" size="sm" fixed-width />
                                </b-btn>
                                <b-btn
                                    v-if="selectedMediaModel.qr_code_svg_url"
                                    variant="outline-primary"
                                    class="my-2 mx-2"
                                    size="sm"
                                    :href="selectedMediaModel.qr_code_svg_url"
                                    target="_blank"
                                >
                                    SVG
                                    <font-awesome-icon :icon="['fas', 'external-link']" size="sm" fixed-width />
                                </b-btn>
                            </div>
                            <b-img
                                v-if="selectedMediaModel.qr_code_url"
                                :src="selectedMediaModel.qr_code_url"
                                class="mx-3"

                                height="auto"
                                width="100px"
                            />
                            <b-btn
                                class="my-2"
                                v-else
                                @click="generateQrCode"
                                :disabled="generating_qr_code"
                            >
                                Generate QR Code
                                <b-spinner
                                    v-if="generating_qr_code"
                                    small
                                />
                            </b-btn>
                        </b-form-group>

                    </b-col>
                </b-row>

            </b-container>

            <div class="row align-items-center">
                <div class="w-100">
                    <MediaPedigree :editing_media_pedigree_data="editing_media_pedigree_data"
                                   :pedigree_loading="pedigree_loading" />
                </div>
            </div>

            <template #modal-footer>
                <div v-if="!uploading_media">
                    <b-button
                        variant="primary"
                        @click="updateMedia"
                        :disabled="validating()"
                    >
                        Update Media
                    </b-button>

                    <span id="disabled-delete-btn">
                        <b-button
                            v-if="is_media_admin"
                            variant="danger"
                            @click="deleteMedia"
                            :disabled="delete_media_disabled || !can_delete_media"
                            class="ml-2"
                        >
                            Delete Media
                        </b-button>
                    </span>
                    <b-tooltip v-if="delete_media_disabled" target="disabled-delete-btn">Media must be removed from all
                        articles.
                    </b-tooltip>
                </div>
                <div v-else>
                    <b-spinner small label="Uploading..."></b-spinner>
                    Uploading File - This may take a few minutes
                </div>
                <b-button
                    variant="light"
                    @click="cancel"
                    :disabled="uploading_media"
                >
                    Cancel
                </b-button>
            </template>

        </b-modal>

        <AddAlternateMedia
            v-model="show_add_alternate_media_modal"
            :value="null"
            :alternate_media_options="alternate_media_options"
            :alternate_media_options_loading="alternate_media_options_loading"
            :media_to_link="selectedMediaModel"
            :media_group="static_media_array"
            @hideAddAlternate="show_add_alternate_media_modal = false"
            @alternateAdded="handleAlternateAdded"
        />
        <CreateAlternateMedia
            v-model="show_create_alternate_media_modal"
            :value="null"
            :media_to_link="selectedMediaModel"
            :media_group="static_media_array"
            @hideCreateAlternate="show_create_alternate_media_modal = false"
            @alternateAdded="handleAlternateAdded"
        />
    </div>
</template>

<style lang="scss" scoped>
.splash-img-container {
    top: 0;
    right: 0;
}

.splash-img {
    opacity: .5;
    transition: .2s ease;
}

.splash-img:hover {
    transform: scale(1.1);
    opacity: 1;
}

.copy-btn {
    right: 25px;
}

.add-language {
    margin-left: 3px;
    margin-right: 3px;
}
</style>

<script>
import TagMultiselect from '@/components/TagMultiselect'
import {mapState} from 'vuex'

import LanguageMultiselect from '@/components/LanguageMultiselect'
import AddAlternateMedia from '@/components/modals/media/AddAlternateMedia'
import CreateAlternateMedia from '@/components/modals/media/CreateAlternateMedia'
import MediaPedigree from '../../media/MediaPedigree.vue'

import languages from '@/common/languages'
import copy from 'copy-html-to-clipboard'
import moment from 'moment'

/** Maximum upload size for replacement media files (bytes). */
const MAX_MEDIA_UPLOAD_BYTES = 300 * 1024 * 1024

export default {
    data: () => ({
        /** Message under New Media `b-form-file` when `submit_data.file` exceeds max size. */
        upload_file_size_alert: null,
        /** [{ key, label }] default splash options loaded from the registry endpoint. */
        default_splash_options: [],
        /** Requirement 2.8 partial-load indicator: fewer than four options or a failed fetch. */
        default_splash_load_failed: false,
        submit_data: {
            title: '',
            description: '',
            alt: '',
            caption: '',
            credit: '',
            is_public_domain: false,
            language: '',
            splash_file: null,
            splash_default_key: null,
            caption_file: null,
            type: 'video',
            file: null,
            url: null,
            is_restricted: false,
            restricted_description: '',
        },

        new_language_media: '',
        languages: languages,
        uploading_media: false,
        alternate_media_options: [],
        alternate_media: [],
        alternate_media_loading: false,
        alternate_media_key: 1,
        copying_to_clipboard: false,
        selected_media_index: 0,

        show_media_pedigree: false,
        alternate_media_search_term: '',
        show_add_alternate_media_modal: false,
        editing_media_pedigree_data: [],
        pedigree_loading: false,
        static_media_array: [],
        mediaChanged: false,
        cached_media: {},
        alternate_media_options_loading: false,
        axiosCancelToken: null,
        show_create_alternate_media_modal: false,
        generating_qr_code: false,
    }),
    props: [
        'value',
        'editing_media',
    ],
    components: {
        TagMultiselect,
        LanguageMultiselect,
        MediaPedigree,
        AddAlternateMedia,
        CreateAlternateMedia
    },
    computed: {
        ...mapState({
            user: state => state.user.user,
            tags: state => state.tags.tags,
            selectedMedia: state => state.media.selectedMedia,
        }),
        can_create_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'create_media')
        },
        can_update_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_media')
        },
        can_delete_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'delete_media')
        },
        can_link_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'link_media')
        },
        can_unlink_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'unlink_media')
        },
        selectedMediaModel: {
            get() {
                return this.selectedMedia;
            },
            set(value) {
                this.$store.commit('setSelectedMedia', value);
            },
        },
        languageOptions() {
            return this.languages.map(item => item.language);
        },
        modal_id() {
            let modal_id = 'edit-media-modal'
            return modal_id
        },
        thumbnail() {
            let thumbnail = null

            if (
                this.selectedMediaModel &&
                this.selectedMediaModel.dam_id
            ) {
                thumbnail = this.selectedMediaModel.url
            } else if (!this.selectedMediaModel.splash_url) {
                thumbnail = this.selectedMediaModel.url;
            } else {

                thumbnail = this.selectedMediaModel.url
            }

            return thumbnail
        },
        delete_media_disabled() {
            let disabled = true;
            if (this.pedigree_loading) {
                disabled = true;
            } else if (!this.editing_media_pedigree_data || this.editing_media_pedigree_data.length == 0) {
                disabled = false;
            }

            return disabled;

        },
        has_english_alt() {
            if (this.selectedMediaModel.language != 'en') {
                const hasEnglishMedia = this.static_media_array.filter(item => item.id !== this.selectedMediaModel.id).some(item => item.language === 'en');

                return hasEnglishMedia;
            }
            return true;
        },
        has_alt_media() {
            if (this.selectedMediaModel.language != 'en') {
                return this.static_media_array.filter(item => item.id !== this.selectedMediaModel.id).some(item => item.language === 'en');
            } else if (this.selectedMediaModel.language != 'es') {
                return this.static_media_array.filter(item => item.id !== this.selectedMediaModel.id).some(item => item.language === 'es');
            }
            return true;
        },
        languageLabel() {
            const languageObject = this.languages.find(lang => lang.language === this.selectedMediaModel.language);
            return languageObject ? languageObject.label : null;
        },
        is_media_admin() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_media')
        },
        copy_to_clipboard_btn_variant() {
            let variant = "light"

            if (this.copying_to_clipboard) {
                variant = 'success'
            }

            return variant
        },
        isExpired() {
            const expirationDate = this.selectedMediaModel && this.selectedMediaModel.expiration_date;
            if (!expirationDate) {
                return false;
            }
            // Compare at end-of-day local time so a same-day date isn’t marked expired prematurely
            const [ymd] = String(expirationDate).split(' ');
            if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
                return false;
            }
            const endOfDay = new Date(`${ymd}T23:59:59`);

            return !isNaN(endOfDay) && Date.now() > endOfDay.getTime();
        },
        formattedCreatedDate() {
            const d = this.selectedMediaModel && this.selectedMediaModel.created;
            if (!d) {
                return null;
            }
            return moment(d).format('MMM D, YYYY h:mm A');
        },
        formattedUpdatedDate() {
            const d = this.selectedMediaModel && this.selectedMediaModel.updated;
            if (!d) {
                return null;
            }
            return moment(d).format('MMM D, YYYY h:mm A');
        },
        createdByLabel() {
            const user = this.selectedMediaModel && this.selectedMediaModel.created_by_user;
            if (!user) {
                return null;
            }
            return `${user.first_name || ''} ${user.last_name || ''}`.trim() || null;
        },
        updatedByLabel() {
            const user = this.selectedMediaModel && this.selectedMediaModel.updated_by_user;
            if (!user) {
                return null;
            }
            return `${user.first_name || ''} ${user.last_name || ''}`.trim() || null;
        },
    },
    watch: {
        selectedMediaModel: {
            handler(newMedia, oldMedia) {
                // this checks to see if the media has been updated/changed
                this.mediaChanged = !(_.isEqual(this.selectedMediaModel, this.cached_media))
            },
            deep: true,
        },
        'selectedMediaModel.id': {
            handler(newId, oldId) {
                // handle all the fetching of new data for the new media when the id changes
                this.getAlternateMediaOptions();
                this.getPedigreeData();
                this.handleVideoAudioRerender();
                this.submit_data.file = null;
                this.submit_data.splash_file = null;
                this.submit_data.caption_file = null
                this.upload_file_size_alert = null
                // Pre-select the matching default (or None/Custom) for the newly selected media.
                this.submit_data.splash_default_key = this.matchDefaultSplashKey();
            },
            deep: true,
        },
        'submit_data.file': {
            handler(newFile) {
                if (this.rejectOversizedMainFile(newFile)) {
                    this.$nextTick(() => {
                        this.submit_data.file = null
                    })
                    return
                }
                if (newFile !== '') {
                    this.mediaChanged = true;
                }
            },
            deep: true,
        },
    },
    methods: {
        fileExceedsMaxUpload(file) {
            return file && typeof file.size === 'number' && file.size > MAX_MEDIA_UPLOAD_BYTES
        },
        /**
         * Validates only `submit_data.file` (New Media). Sets `upload_file_size_alert` when over limit.
         * Does not clear the warning when `file` is empty — that happens after rejecting an oversized file and
         * clearing the input, which would otherwise flash the alert away.
         * @returns {boolean} true if the file should be rejected (oversized).
         */
        rejectOversizedMainFile(file) {
            if (!this.fileExceedsMaxUpload(file)) {
                if (file && typeof file.size === 'number') {
                    this.upload_file_size_alert = null
                }
                return false
            }
            const mb = MAX_MEDIA_UPLOAD_BYTES / (1024 * 1024)
            this.upload_file_size_alert = `Media file must be ${mb} MB or smaller.`
            return true
        },
        uploadsWithinMaxSize() {
            return !this.rejectOversizedMainFile(this.submit_data.file)
        },
        async loadDefaultSplashes() {
            try {
                const { data } = await this.axios.get('/api/media/default_splashes')
                this.default_splash_options = (data && data.default_splashes) || []
                this.default_splash_load_failed = this.default_splash_options.length < 4
            } catch (e) {
                this.default_splash_options = []
                this.default_splash_load_failed = true
            }
            // Pre-select the option matching the record's current splash_url (if any).
            this.submit_data.splash_default_key = this.matchDefaultSplashKey()
        },
        /**
         * Return the default splash key whose canonical URL matches the current
         * record's splash_url, or null for an empty/custom splash. Comparison is
         * case-insensitive and tolerant of a single trailing slash.
         */
        matchDefaultSplashKey() {
            const splashUrl = this.selectedMediaModel && this.selectedMediaModel.splash_url
            if (!splashUrl) {
                return null
            }
            const normalize = (url) => (url || '').toLowerCase().replace(/\/$/, '')
            const target = normalize(splashUrl)
            const match = this.default_splash_options.find((opt) => normalize(opt.url) === target)
            return match ? match.key : null
        },
        onDefaultSplashChange(key) {
            // Switching to a default records the key and clears any chosen file (Req 8.4, 5.1).
            if (key) {
                this.submit_data.splash_file = null
            }
            // Selecting None/custom re-enables the file input (Req 5.2); key is already null.
            this.submit_data.splash_default_key = key || null
        },
        onSplashFileChange(file) {
            // Choosing a file resets the picker to None/custom (Req 5.3).
            if (file) {
                this.submit_data.splash_default_key = null
            }
        },
        selectMedia(index) {
            if (this.uploading_media) {
                return
            }
            // check to see if current selected media changed
            if (this.mediaChanged) {
                this.$swal({
                    html: 'There are unsaved changes for this media, would you like to save before switching media languages?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Update Media and Switch Languages',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then(async (result) => {
                    if (result.value && await this.updateMedia()) {
                        this.switchMedia(index);
                    }
                })
            } else {
                this.switchMedia(index);
            }
        },
        handleAlternateAdded() {
            this.getAlternateMedia();
        },
        switchMedia(index) {
            // save modified data to the static_media_array
            this.static_media_array[this.selected_media_index] = Object.assign({}, this.selectedMediaModel)
            // change index
            this.selected_media_index = index;
            // set the selected Media
            this.$store.commit("setSelectedMedia", this.static_media_array[index]);
            if (Array.isArray(this.selectedMediaModel.merlin_ids)) {
                this.selectedMediaModel.merlin_ids = this.selectedMediaModel.merlin_ids.join(', ')
            }
            // cache media to track changes for new selectedMedia
            this.cached_media = Object.assign({}, this.selectedMediaModel);
            if (Array.isArray(this.cached_media.merlin_ids)) {
                this.cached_media.merlin_ids = this.cached_media.merlin_ids.join(', ')
            }
        },
        handlePublicDomainToggle(newVal) {
            // If the user is toggling on and there is an existing credit, show a confirmation dialog
            if (newVal && this.selectedMediaModel.credit) {
                this.$swal({
                    html: 'Are you sure you want to set this media to public domain? Doing so will delete any credit that currently exists for this media.',
                    showCancelButton: true,
                    confirmButtonText: 'Set As Public Domain',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.selectedMediaModel.is_public_domain = newVal;
                        this.selectedMediaModel.credit = '';
                    } else {
                        this.selectedMediaModel.is_public_domain = !newVal;
                    }
                })

            } else {
                this.selectedMediaModel.is_public_domain = newVal;
            }
        },
        validating() {
            const {language, title, merlin_ids, alt, type} = this.selectedMediaModel;
            const languageCheck = this.languageCheck(this.selectedMediaModel.language);

            if (type === 'image') {
                return !(language && title && alt && languageCheck);
            }

            return !(language && title && languageCheck);
        },

        async addTag(tag) {
            await this.axios.post('/api/media/add_tag/' + this.selectedMediaModel.id, {
                tag_id: tag.id
            })
            this.$emit('getMedia');
        },
        async removeTag(tag) {
            await this.axios.post('/api/media/remove_tag/' + this.selectedMediaModel.id, {
                tag_id: tag.id
            })
            this.$emit('getMedia');
        },
        onModalShown() {
            // Reliable open hook: the b-modal `shown` event fires regardless of how the
            // parent wires visibility (this modal is opened via v-model, with `value` pinned
            // to null, so a `value` watcher would never fire). loadDefaultSplashes() also
            // pre-selects the option matching the record's current splash_url once options load.
            this.loadDefaultSplashes();
            this.focusSearch();
        },
        focusSearch() {
            if (
                this.$refs['related-media-search'] &&
                this.$refs['related-media-search'].$refs['search-term']
            ) {
                this.$refs['related-media-search'].$refs['search-term'].focus()
            }
        },
        cancel(event) {
            event.preventDefault();
            if (this.uploading_media) {
                return
            }
            if (this.mediaChanged) {
                this.$swal({
                    html: 'There are unsaved changes for this media, would you like to save before closing?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Update Media and Close',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.updateMedia(event)
                    } else {
                        this.hide()
                    }
                })
            } else {
                this.selected_media_index = 0;
                this.cached_media = Object.assign({}, this.selectedMediaModel);
                this.$emit('getMedia');
                this.$emit('closeUpload');
                this.hide()
            }
        },
        hide() {
            this.selected_media_index = 0;
            this.cached_media = Object.assign({}, this.selectedMediaModel);
            this.$emit('hideUploadMedia')
        },
        hidden() {
            this.selected_media_index = 0;
            this.cached_media = Object.assign({}, this.selectedMediaModel);
            this.$emit('hideUploadMedia')
        },
        getAlternateMediaOptions() {
            this.alternate_media_options_loading = true;
            this.axios.get('/api/media/get_alternate_media_options/' + this.selectedMediaModel.type + '/' + this.selectedMediaModel.language)
                .then(response => {
                    if (response.data) {
                        this.alternate_media_options = response.data;
                    }
                    this.alternate_media_options_loading = false;
                })
                .catch(error => {
                    if (error) {
                        this.alternate_media_options_loading = false;
                    }
                })
        },
        async getPedigreeData() {
            // Check if there's an ongoing API call
            if (this.axiosCancelToken) {
                // Cancel the ongoing API call
                this.axiosCancelToken.cancel('Operation canceled by the user.');
            }

            // Set up a new cancellation token for the current request
            this.axiosCancelToken = this.axios.CancelToken.source();

            // Start the new API call

            try {
                this.pedigree_loading = true;
                const response = await this.axios.post('/api/media/get_all_media_pedigree/' + this.selectedMediaModel.id, {
                    cdn_url: this.selectedMediaModel.url
                }, {
                    cancelToken: this.axiosCancelToken.token,
                });

                if (response.data) {
                    this.editing_media_pedigree_data = response.data;
                    this.pedigree_loading = false;
                }
            } catch (error) {
                if (error && !this.axios.isCancel(error)) {
                    this.editing_media_pedigree_data = [];
                    this.pedigree_loading = false;
                }
            }
        },
        getFlagIso(language) {
            const languageObject = this.languages.find(lang => lang.language === language);
            return languageObject ? languageObject.flag_iso : null;
        },
        async updateMedia(event) {
            if (this.uploading_media || !this.uploadsWithinMaxSize()) {
                return false
            }
            this.uploading_media = true

            try {
                const form_data = this.buildFormData();
                await this.axios.post('/api/media/update_media/', form_data)
                this.submit_data.file = null
                this.submit_data.splash_file = null
                this.submit_data.caption_file = null
                this.cached_media = _.cloneDeep(this.selectedMediaModel)
                await this.$nextTick()
                this.mediaChanged = false
                this.$bvToast.toast('Media was successfully updated', {
                    title: 'Success',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                if (event) {
                    this.$emit('link', this.selectedMediaModel);
                    this.$emit('hideUploadMedia');
                    this.$emit('mediaUploadSuccess');
                    this.$emit('getMedia');
                    this.hide();
                }
                return true
            } catch (e) {
                const message = e.response && e.response.data && e.response.data.message
                    ? e.response.data.message : `Failed to update media: ${e.message}`
                this.$bvToast.toast(message, {title: 'Error', variant: 'danger', solid: false})
                await this.$store.dispatch('pushError', {
                    data: {message},
                    time: moment().toISOString(),
                })
                return false
            } finally {
                this.uploading_media = false
            }
        },
        buildFormData() {
            const form_data = new FormData();

            if (this.submit_data.file) {
                form_data.append('file', this.submit_data.file)
            }
            if (this.submit_data.splash_default_key) {
                form_data.append('splash_default_key', this.submit_data.splash_default_key)  // Req 5.4
            } else if (this.submit_data.splash_file) {
                form_data.append('splash_file', this.submit_data.splash_file)  // Req 5.5
            }
            // neither => omit both => no splash change (Req 5.6)
            if (this.submit_data.caption_file) {
                form_data.append('caption_file', this.submit_data.caption_file)
            }
            if (this.selectedMediaModel.title && this.selectedMediaModel.title !== '') {
                form_data.append('title', this.selectedMediaModel.title)
            }
            if (this.selectedMediaModel.language && this.selectedMediaModel.language !== '') {
                form_data.append('language', this.selectedMediaModel.language)
            }
            if (this.selectedMediaModel.type && this.selectedMediaModel.type !== '') {
                form_data.append('type', this.selectedMediaModel.type)
            }
            if (this.selectedMediaModel.description && this.selectedMediaModel.description !== '') {
                form_data.append('description', this.selectedMediaModel.description)
            }
            if (this.selectedMediaModel.alt && this.selectedMediaModel.alt !== '') {
                form_data.append('alt', this.selectedMediaModel.alt)
            }
            if (this.selectedMediaModel.merlin_ids) {
                // format comma separated merlin_ids input into an array, only accept numbers
                const merlin_id_array = this.selectedMediaModel.merlin_ids.split(',')
                if (merlin_id_array.length > 0) {
                    form_data.append('merlin_ids', merlin_id_array)
                }
            }
            if (this.selectedMediaModel.caption && this.selectedMediaModel.caption !== '') {
                form_data.append('caption', this.selectedMediaModel.caption)
            }
            if (this.selectedMediaModel.is_public_domain) {
                form_data.append('is_public_domain', this.selectedMediaModel.is_public_domain)
            }
            if (this.selectedMediaModel.credit && this.selectedMediaModel.credit !== '') {
                form_data.append('credit', this.selectedMediaModel.credit)
            }
            if (this.selectedMediaModel.designer_notes && this.selectedMediaModel.designer_notes !== '') {
                form_data.append('designer_notes', this.selectedMediaModel.designer_notes)
            }
            if (this.selectedMediaModel.merlin_title && this.selectedMediaModel.merlin_title !== '') {
                form_data.append('merlin_title', this.selectedMediaModel.merlin_title)
            }
            if (this.selectedMediaModel.expiration_date && this.selectedMediaModel.expiration_date !== '') {
                form_data.append('expiration_date', this.selectedMediaModel.expiration_date)
            }
            form_data.append('expiration_date_info', this.selectedMediaModel.expiration_date_info || '')
            if (this.selectedMediaModel.restricted_description && this.selectedMediaModel.restricted_description !== '') {
                form_data.append('restricted_description', this.selectedMediaModel.restricted_description)
            }

            form_data.append('is_restricted', this.selectedMediaModel.is_restricted)
            form_data.append('media_id', this.selectedMediaModel.id)

            return form_data;
        },
        addAlternateMedia() {
            this.show_add_alternate_media_modal = true;
        },
        createAlternateMedia() {
            this.show_create_alternate_media_modal = true;
        },
        deleteMedia(event) {
            this.axios.post('/api/media/delete_media/' + this.selectedMediaModel.id)
                .then(response => {
                    if (!response) {
                        this.$bvToast.toast('An error has ocurred', {
                            title: 'Error',
                            variant: 'danger',
                            solid: false,
                            autoHideDelay: 3000,
                            appendToast: true
                        })
                    } else {
                        this.$bvToast.toast('Media was successfully deleted', {
                            title: 'Success',
                            variant: 'success',
                            solid: false,
                            autoHideDelay: 3000,
                            appendToast: true
                        })
                    }

                    this.$emit('getMedia');
                    this.hide();

                })
                .catch(error => {
                    if (error) {
                        this.uploading_media = false;
                    }
                });

        },
        getAlternateMedia() {
            this.alternate_media_loading = true;
            this.axios.get('/api/media/get_alternate_media/' + this.editing_media.id + '/' + null)
                .then(response => {
                    if (response.data) {
                        this.alternate_media = response.data;
                        this.static_media_array = [this.editing_media, ...this.alternate_media];
                    }
                    this.alternate_media_loading = false;
                })
                .catch(error => {
                    if (error) {
                        this.alternate_media = [];
                    }
                    this.alternate_media_loading = false;
                })
        },
        handleVideoAudioRerender() {

            const videoAudioElement = this.$refs.videoAudioElement;
            if (videoAudioElement) {
                videoAudioElement.src = this.selectedMediaModel.url;
                videoAudioElement.load();
            }
        },
        unlinkFromAlternateMedia() {
            //unlink from all media - set group_id to null
            this.axios.post('/api/media/unlink_media_from_group/' + this.selectedMediaModel.id)
                .then(response => {
                    if (!response) {
                        this.$bvToast.toast('An error has ocurred', {
                            title: 'Error',
                            variant: 'danger',
                            solid: false,
                            autoHideDelay: 3000,
                            appendToast: true
                        })
                    } else {
                        this.$bvToast.toast('Media was successfully unlinked', {
                            title: 'Success',
                            variant: 'success',
                            solid: false,
                            autoHideDelay: 3000,
                            appendToast: true
                        })
                    }
                    this.selectMedia(0);
                    this.getAlternateMedia();
                })
        },
        copyToClipboard(media) {
            let media_url = media.url;
            if (!(media_url.startsWith("http:") || media_url.startsWith("https:"))) {
                media_url = `https:${media_url}`;
            }
            copy(media_url, {
                asHtml: false
            })

            this.copying_to_clipboard = true

            setTimeout(() => {
                this.copying_to_clipboard = false
            }, 1500)
        },
        languageCheck(language) {
            return !this.static_media_array.filter(item => item.id !== this.selectedMediaModel.id).some(item => item.language === language)
        },
        async generateQrCode() {
            try {
                this.generating_qr_code = true
                const response = await this.axios.post(`/api/media/qr_code/${this.selectedMediaModel.id}`)
                this.selectedMediaModel.qr_code_url = response.data.qr_code_url
                this.selectedMediaModel.qr_code_svg_url = response.data.qr_code_svg_url
                this.cached_media.qr_code_url = response.data.qr_code_url // we don't need to send an update for this field
                this.cached_media.qr_code_svg_url = response.data.qr_code_svg_url // we don't need to send an update for this field
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to generate qr code: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.generating_qr_code = false
            }
        },
        onMediaExpirationDateInput(event) {
            const ymd = event.target.value;
            const time =
                (this.selectedMediaModel.expiration_date || '').split(' ')[1] ||
                '00:00:00';

            this.selectedMediaModel.expiration_date = ymd
                ? `${ymd} ${time}`
                : null;
        },
    },
    mounted() {
        // converts merlin ids from array to csv
        if (Array.isArray(this.selectedMediaModel.merlin_ids)) {
            this.selectedMediaModel.merlin_ids = this.selectedMediaModel.merlin_ids.join(', ')
        }
        this.getPedigreeData();
        this.getAlternateMediaOptions();
        this.getAlternateMedia();
        this.cached_media = this.editing_media;
    },
}
</script>
